import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AppSettingsService, AppStorage } from '../../../app/core/services';
import { OperationPermissionGroupService } from '../../../libs/role';
import { LicenceService } from '../../../libs/system';
import { AuthActions } from './auth.actions';
import { Store } from '@ngrx/store';
import { AuthSelectors } from '.';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthApiService } from '@rillion/auth';
import { OperationPermissions } from '@rillion/shared/enums/operation-permission';
import { Userpilot } from 'userpilot';

@Injectable()
export class AuthEffects {
  constructor(
    private readonly store: Store,
    private readonly permissionService: OperationPermissionGroupService,
    private readonly licenceService: LicenceService,
    private readonly authApiService: AuthApiService,
    private readonly appStorage: AppStorage,
    private readonly actions$: Actions,
    private readonly appSettingsService: AppSettingsService,
  ) {}

  getUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUser),
      switchMap(() =>
        this.authApiService.getUserData().pipe(
          map((user) => {
            const role = this.appStorage.loadDataSync('role');

            // TODO: Don't do this, it's a hack
            if (role) {
              user.role = role.toString();
            }

            return AuthActions.getUserSuccess({ user });
          }),
          catchError((error: HttpErrorResponse) => of(AuthActions.getUserFailure(error))),
        ),
      ),
    );
  });

  getRolesOnUserChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserSuccess),
      map(() => AuthActions.getRoles()),
    );
  });

  getRoles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getRoles),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectUser)),
      switchMap(([, { user }]) =>
        this.authApiService.getRolesForUser(user).pipe(
          map((roles) => AuthActions.getRolesSuccess({ roles: roles.map((role) => role.role) })),
          catchError((error: HttpErrorResponse) => of(AuthActions.getRolesFailure(error))),
        ),
      ),
    );
  });

  setInitialRoles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getRolesSuccess),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectUser)),
      map(([roles, user]) => AuthActions.updateUserRole({ user: { ...user, role: roles.roles[0] } })),
    );
  });

  getLicenceOnUserChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserSuccess),
      map(() => AuthActions.getLicences()),
    );
  });

  getLicenceOnRoleChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.setUserRole),
      map(() => AuthActions.getLicences()),
    );
  });

  getLicences$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getLicences),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectUser)),
      switchMap(([, { environment }]) =>
        this.licenceService.apiV10LicenceGet(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, environment).pipe(
          map((licences) => AuthActions.getLicencesSuccess({ licences })),
          catchError((error: HttpErrorResponse) => of(AuthActions.getLicencesFailure(error))),
        ),
      ),
    );
  });

  getPermissionsOnUserChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserSuccess),
      map(() => AuthActions.getPermissions()),
    );
  });

  getPermissionsOnRoleChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.setUserRole),
      map(() => AuthActions.getPermissions()),
    );
  });

  getPermissions = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getPermissions),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectUser)),
      switchMap(([, { user, role }]) =>
        this.permissionService.apiV10RoleOperationpermissiongroupGet(user, role).pipe(
          map((permissions) => AuthActions.getPermissionsSuccess({ permissions })),
          catchError((error: HttpErrorResponse) => of(AuthActions.getPermissionsFailure(error))),
        ),
      ),
    );
  });

  userPilotIdentityOnUser$ = createEffect(
    () => {
      return this.store.select(AuthSelectors.selectUserAndPermissions).pipe(
        filter(({ user, permissions }) => !!user && !!permissions),
        tap(({ user: { email, user, role, environment, languageCode: languageid }, permissions }) => {
          const company = { id: environment };
          const invoiceLogPermission = permissions[OperationPermissions.InvoiceQueue];
          Userpilot.initialize(this.appSettingsService.appSettings.appToken);
          Userpilot.identify(email, { email, user, role, company, languageid, invoiceLogPermission });
        }),
      );
    },
    { dispatch: false },
  );

  updateAppStorageOnRoleChange$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.setUserRole),
        tap(({ role }) => {
          this.appStorage.saveData('role', role);
          this.authApiService.generateAccessToken(role);
        }),
      );
    },
    { dispatch: false },
  );
}
